// Shared labels + held-reason logic for the AI import Staging & Review gate.
// Mirrors the backend _skip_key ranking so the reconciliation receipt ties out
// exactly with what the server commits vs holds.

export const SKIP_LABELS = {
  blocked: "Blocked",
  not_calculated: "Not calculated",
  duplicate: "Duplicate",
  fx_review: "Needs review — FX rate",
  negative_qty: "Needs review — negative",
  magnitude_outlier: "Needs review — unusually large",
  boundary_review: "Needs review — leased-asset boundary",
  derived_total: "Derived total",
  rollup_annual: "Annual roll-up",
  low_confidence: "Needs review",
  not_esg: "Not ESG",
  deselected: "Deselected",
  unclassified: "Unclassified",
};

export const REASON_HELP = {
  blocked: "Unit/scale mismatch would distort the result — a genuine stop.",
  not_calculated: "No emission factor or computable result — imported, never shown as a verified figure.",
  duplicate: "Same document + period already present — held to avoid double-counting.",
  fx_review: "Foreign-currency line needs a valid in-file FX rate (not dated before the posting month) before it can commit.",
  negative_qty: "Negative quantity — may be a credit note or a data error; held for a human decision.",
  magnitude_outlier: "Order-of-magnitude outlier for this activity — held so a 10× line can't slip in.",
  boundary_review: "Landlord-supplied / leased-asset energy — confirm the Scope boundary (2 vs 3 Cat 8) under operational control.",
  derived_total: "Looks like a pre-calculated total — importing as activity data would double-count.",
  rollup_annual: "Whole-year summary of the monthly rows — held so the yearly total isn't double-counted.",
  low_confidence: "Low AI confidence — held for a human decision.",
  not_esg: "Not ESG-relevant operational data.",
  deselected: "Unticked by the reviewer.",
  unclassified: "No pillar/category assigned.",
};

// Returns null when the row WILL commit, else the reason it is held. The precedence
// below mirrors the backend _skip_key EXACTLY so chips, the confirm dialog, the result
// screen, the Recover list and the skipped CSV all tie out (R11/R13).
export const heldReason = (r, hasAck) => {
  const committed = r.include && r.pillar && r.category && !(r.blocked && !hasAck) && !r.not_calculated;
  if (committed) return null;
  if (r.blocked) return "blocked";
  if (r.duplicate) return "duplicate";
  if (r.not_calculated) return "not_calculated";
  if (r.fx_review) return "fx_review";
  if (r.negative_qty) return "negative_qty";
  if (r.magnitude_outlier) return "magnitude_outlier";
  if (r.boundary_review) return "boundary_review";
  if (r.derived_total) return "derived_total";
  if (r.rollup_annual) return "rollup_annual";
  if (!r.is_esg) return "not_esg";
  if (r.low_confidence) return "low_confidence";
  if (!r.pillar || !r.category) return "unclassified";
  return "deselected";
};

// Per-line plain-language explanation for a flagged row: {rule, evidence, fix}.
// Deterministic, mirrors the backend guards — never an AI guess. Returns null when
// the row carries no flag worth explaining.
export const flagExplanation = (r) => {
  const pct = Math.round((r.confidence || 0) * 100);
  if (r.blocked) return {
    rule: "Unit & scale guard — the value's unit doesn't match the chosen emission factor's unit.",
    evidence: r.blocked_reason || `Value is in "${r.unit || "?"}" but the factor is per "${r.factor_unit || "?"}".`,
    fix: r.blocked_convertible && r.suggested_unit
      ? `Apply the one-tap convert to ${r.suggested_value != null ? `${r.suggested_value} ` : ""}${r.suggested_unit}, or correct the unit/factor. To commit as-is, type an override acknowledgement.`
      : "Correct the unit or pick a factor in the same measure. To commit as-is, type an override acknowledgement.",
  };
  if (r.not_calculated) return {
    rule: "No emission factor or computable result for this Environment line.",
    evidence: r.factor_code ? `Factor "${r.factor_code}" has no usable kgCO₂e value.` : "No emission factor selected.",
    fix: "Assign a matching factor to calculate it, or include it deliberately as an uncalculated record.",
  };
  if (r.duplicate) return {
    rule: r.dup_scope === "in_file"
      ? "The same document/entry appears more than once in this file."
      : "This row matches a record already in the inventory.",
    evidence: r.dup_detail || "Document number + posting period match.",
    fix: "Leave it held to avoid double-counting, or re-tick to import it deliberately.",
  };
  if (r.fx_review) return {
    rule: "Foreign-currency line needs a valid in-file FX rate before it can commit.",
    evidence: r.fx_reason || "The in-file FX rate is missing, zero, or dated before the posting month.",
    fix: "Supply a valid in-file rate dated in or after the posting month, then re-tick to import.",
  };
  if (r.negative_qty) return {
    rule: "Negative quantity — held for review.",
    evidence: `Quantity is ${r.value}. It may be a genuine credit note or a data error.`,
    fix: "Re-tick to commit a legitimate credit, or correct the quantity.",
  };
  if (r.magnitude_outlier) return {
    rule: "Unusually large — an order-of-magnitude outlier for this activity (independent of AI confidence).",
    evidence: r.magnitude_reason || "Much larger than the typical value for this activity and site.",
    fix: "Re-tick if the figure is genuinely correct, or fix the quantity/unit.",
  };
  if (r.boundary_review) return {
    rule: "Landlord-supplied / leased-asset energy — the Scope boundary needs a human decision.",
    evidence: "Looks like leased, landlord-supplied, common-area or service-charge energy.",
    fix: "Confirm Scope 2 vs Scope 3 Category 8 (upstream leased assets) under your operational-control approach, then re-tick.",
  };
  if (r.derived_total) return {
    rule: "Looks like a pre-calculated emissions total, not raw activity data.",
    evidence: "The label/unit reads like a computed tCO₂e figure.",
    fix: "Import the underlying activity rows instead, or re-tick only if this really is raw activity.",
  };
  if (r.rollup_annual) return {
    rule: "Whole-year roll-up sitting alongside the monthly rows for the same metric.",
    evidence: `Annual period (${r.period || "YYYY"}) duplicates the sum of the monthly detail.`,
    fix: "Keep it held so the yearly total isn't double-counted, or re-tick to import the annual figure deliberately.",
  };
  if (r.low_confidence) return {
    rule: "AI classification confidence is below the review threshold.",
    evidence: `Confidence ${pct}%${r.reason ? ` — ${r.reason}` : ""}.`,
    fix: "Check the pillar/category/factor, then re-tick to import.",
  };
  if (!r.is_esg) return {
    rule: "Not ESG-relevant operational data.",
    evidence: r.reason || "No ESG signal was detected on this row.",
    fix: "Leave it excluded, or re-tick to force-include it if it is in fact ESG data.",
  };
  return null;
};

export const rowKey = (r) => (r.__sheet ? `${r.__sheet}::${r.idx}` : String(r.idx));
